import { type FormEvent, useEffect, useMemo, useState } from "react";
import QRCode from "qrcode";
import { api } from "../lib/api";
import type { Account } from "../lib/types";

interface LoginSelection {
  selectionRequired: true;
  challengeId: string;
  options: Array<{ id: string; displayName: string; organizationName?: string }>;
}

interface MfaRequired {
  mfaRequired: true;
  challengeId: string;
}

interface MfaSetupRequired {
  mfaSetupRequired: true;
  challengeId: string;
  secret: string;
  otpauthUri: string;
}

type LoginResult = { account: Account } | LoginSelection | MfaRequired | MfaSetupRequired;

function organizationCodeFromPath() {
  const match = window.location.pathname.match(/^\/login\/([^/]+)\/?$/i);
  return match ? decodeURIComponent(match[1]!) : null;
}

export function Login({ onLogin, notice }: { onLogin: (account: Account) => void; notice?: string }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [selection, setSelection] = useState<LoginSelection | null>(null);
  const [mfa, setMfa] = useState<MfaRequired | MfaSetupRequired | null>(null);
  const [mfaCode, setMfaCode] = useState("");
  const [mfaQrImage, setMfaQrImage] = useState("");
  const [recovery, setRecovery] = useState<{ account: Account; codes: string[] } | null>(null);
  const [organizationName, setOrganizationName] = useState("");
  const organizationCode = useMemo(organizationCodeFromPath, []);

  useEffect(() => {
    if (!mfa || !("mfaSetupRequired" in mfa)) {
      setMfaQrImage("");
      return;
    }
    QRCode.toDataURL(mfa.otpauthUri, { width: 280, margin: 2, errorCorrectionLevel: "M" })
      .then(setMfaQrImage)
      .catch(() => setError("Der MFA-Einrichtungscode konnte nicht dargestellt werden."));
  }, [mfa]);

  useEffect(() => {
    if (organizationCode) {
      api<{ name: string }>(`/auth/login-context/${encodeURIComponent(organizationCode)}`)
        .then((result) => setOrganizationName(result.name))
        .catch((caught) => setError(caught instanceof Error ? caught.message : "Der Einrichtungslink ist nicht gültig."));
    }
    const url = new URL(window.location.href);
    const hashParams = new URLSearchParams(url.hash.replace(/^#/, ""));
    const qrToken = hashParams.get("qr_login") ?? url.searchParams.get("qr_login");
    if (!qrToken) return;
    setBusy(true);
    api<{ account: Account }>("/auth/qr-login", { method: "POST", body: JSON.stringify({ token: qrToken }) })
      .then((result) => {
        url.searchParams.delete("qr_login");
        hashParams.delete("qr_login");
        url.hash = hashParams.toString() ? `#${hashParams.toString()}` : "";
        window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
        onLogin(result.account);
      })
      .catch((caught) => setError(caught instanceof Error ? caught.message : "Der QR-Zugang ist nicht gültig."))
      .finally(() => setBusy(false));
  }, [onLogin, organizationCode]);

  function handleLoginResult(result: LoginResult) {
    if ("selectionRequired" in result) setSelection(result);
    else if ("mfaRequired" in result || "mfaSetupRequired" in result) {
      setSelection(null);
      setPassword("");
      setMfaCode("");
      setMfa(result);
    } else onLogin(result.account);
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      const endpoint = organizationCode
        ? `/auth/login/organization/${encodeURIComponent(organizationCode)}`
        : "/auth/login";
      handleLoginResult(await api<LoginResult>(endpoint, {
        method: "POST",
        body: JSON.stringify({ username, password }),
      }));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Die Anmeldung ist fehlgeschlagen.");
    } finally {
      setBusy(false);
    }
  }

  async function chooseAccount(optionId: string) {
    if (!selection) return;
    setBusy(true);
    setError("");
    try {
      handleLoginResult(await api<LoginResult>("/auth/login/select", {
        method: "POST",
        body: JSON.stringify({ challengeId: selection.challengeId, optionId }),
      }));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Das Konto konnte nicht ausgewählt werden.");
      setSelection(null);
    } finally {
      setBusy(false);
    }
  }

  async function verifyMfa(event: FormEvent) {
    event.preventDefault();
    if (!mfa) return;
    setBusy(true);
    setError("");
    try {
      const setup = "mfaSetupRequired" in mfa;
      const result = await api<{ account: Account; recoveryCodes?: string[] }>(setup ? "/auth/mfa/setup/confirm" : "/auth/mfa/verify", {
        method: "POST",
        body: JSON.stringify({ challengeId: mfa.challengeId, code: mfaCode }),
      });
      if (result.recoveryCodes) setRecovery({ account: result.account, codes: result.recoveryCodes });
      else onLogin(result.account);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Der MFA-Code konnte nicht geprüft werden.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="login-page">
      <section className="login-card">
        <img className="login-mascot" src="/brand/lepi-schreibend.webp" alt="" />
        <img className="login-logo" src="/brand/calepia_logo_schrift.webp" alt="CaLega – die lerntherapeutische Übungsplattform" />
        <h1>Schön, dass du da bist.</h1>
        <p className="muted">{organizationName ? `Anmeldung für ${organizationName}` : "Melde dich mit deinem Konto an."}</p>
        {notice && <div className="alert info" role="status">{notice}</div>}

        {recovery ? (
          <div className="login-selection">
            <h2>Wiederherstellungscodes</h2>
            <p>Speichere diese Codes jetzt sicher. Jeder Code kann genau einmal verwendet werden.</p>
            <div className="mfa-recovery-codes">{recovery.codes.map((code) => <code key={code}>{code}</code>)}</div>
            <button className="primary full" onClick={() => onLogin(recovery.account)}>Codes sind sicher gespeichert</button>
          </div>
        ) : mfa ? (
          <form onSubmit={verifyMfa}>
            {"mfaSetupRequired" in mfa ? <>
              <h2>Admin-Konto absichern</h2>
              <p>Scanne diesen Code mit Google Authenticator oder einer anderen TOTP-App.</p>
              {mfaQrImage && <img className="mfa-setup-qr" src={mfaQrImage} alt="MFA-Einrichtungscode" />}
              <details><summary>Geheimnis manuell eingeben</summary><code className="mfa-secret">{mfa.secret}</code></details>
            </> : <>
              <h2>Zwei-Faktor-Anmeldung</h2>
              <p>Gib den sechsstelligen Code aus deiner Authenticator-App oder einen Wiederherstellungscode ein.</p>
            </>}
            <label>
              Sicherheitscode
              <input value={mfaCode} onChange={(event) => setMfaCode(event.target.value)} autoComplete="one-time-code" inputMode="numeric" autoFocus />
            </label>
            {error && <div className="alert error">{error}</div>}
            <button className="primary full" disabled={busy}>{busy ? "Code wird geprüft …" : "Bestätigen"}</button>
          </form>
        ) : selection ? (
          <div className="login-selection">
            <p>Diese Zugangsdaten gehören zu mehreren Konten. Wähle die passende Einrichtung:</p>
            {selection.options.map((option) => (
              <button className="secondary full" disabled={busy} key={option.id} onClick={() => chooseAccount(option.id)}>
                <strong>{option.displayName}</strong>
                {option.organizationName && <span>{option.organizationName}</span>}
              </button>
            ))}
            <button className="text-button" onClick={() => setSelection(null)}>Zurück zur Anmeldung</button>
          </div>
        ) : (
          <form onSubmit={submit}>
            <label>Benutzername<input value={username} onChange={(event) => setUsername(event.target.value)} autoComplete="username" /></label>
            <label>
              Passwort
              <input type="password" value={password} onChange={(event) => setPassword(event.target.value)} autoComplete="current-password" autoFocus />
            </label>
            {error && <div className="alert error">{error}</div>}
            <button className="primary full" disabled={busy}>{busy ? "Anmeldung läuft …" : "Anmelden"}</button>
          </form>
        )}
        {selection && error && <div className="alert error">{error}</div>}
        <p className="login-claim">Schritt für Schritt zum Lernerfolg.</p>
        <nav className="login-legal-links" aria-label="Rechtliches">
          <a href="/">Startseite</a><a href="#/legal/impressum">Impressum</a><a href="#/legal/datenschutz">Datenschutz</a><a href="/THIRD-PARTY-NOTICES.txt">Lizenzen</a>
        </nav>
      </section>
    </main>
  );
}
